import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, abrirFluxoMonitor } from "../api";
import { ordenarPorNome } from "../ordem";
import {
  IconActivity,
  IconAlert,
  IconCheck,
  IconClose,
  IconGauge,
  IconPause,
  IconPlay,
  IconSearch,
  IconShieldCheck,
  IconTrash,
  IconChevronRight,
} from "../icons";
import ErrorState from "./ErrorState";

/**
 * MONITOR — os sistemas ao vivo.
 *
 * De cima para baixo: os quatro indicadores (quantos estão no ar, quanto estão
 * escrevendo, quantos erros, como está a máquina), um cartão por sistema com o
 * farol e a tendência da última meia hora, e o console com as linhas chegando.
 * Clicar num sistema filtra o console; clicar de novo solta.
 *
 * Tudo chega por UM fluxo (SSE) aberto com `fetch`, e não com `EventSource`:
 * o EventSource não manda cabeçalho, e a sessão assinada vai no cabeçalho.
 * Caiu a conexão (deploy, rede), a tela religa sozinha, esperando um pouco
 * mais a cada tentativa, e emenda o que perdeu sem apagar o que já mostrava.
 *
 * As linhas novas não são pintadas uma a uma: entram numa fila e a tela
 * redesenha quatro vezes por segundo — um despejo de log não pode travar o
 * navegador. "Pausar" segura a fila (e mostra quantas esperam); a conexão
 * continua aberta.
 *
 * O motor está em server/monitor.ts — inclusive a lista de sistemas, que vem
 * no resumo: sistema novo aparece aqui sem mexer nesta tela.
 */

type Canal = "saida" | "erro";
type Nivel = "info" | "aviso" | "erro";
type Status = "ok" | "parcial" | "fora" | "desconhecido";

interface Linha {
  id: number;
  em: number | null;
  sistema: string;
  canal: Canal;
  nivel: Nivel;
  texto: string;
  antes?: boolean;
}

interface Saude {
  status: Status;
  ms: number | null;
  desde: number | null;
  checadoEm: number | null;
  quedas24h: number;
  detalhes: { nome: string; ok: boolean; ms: number | null }[];
}

interface SistemaResumo {
  id: string;
  nome: string;
  porta: number;
  temLog: boolean;
  saude: Saude;
  linhasMin: number;
  erros5min: number;
  serie: number[];
  serieErros: number[];
  ultimaLinhaEm: number | null;
  ultimoErro: { em: number; texto: string } | null;
}

interface Maquina {
  cpu: number | null;
  nucleos: number;
  carga: number | null;
  memTotal: number;
  memUsada: number;
  discoTotal: number | null;
  discoUsado: number | null;
  discoOnde: string | null;
  ligadaHa: number;
  so: string;
}

interface Resumo {
  em: number;
  sistemas: SistemaResumo[];
  totais: {
    linhasMin: number;
    erros5min: number;
    serie: number[];
    serieErros: number[];
    noAr: number;
    parciais: number;
    fora: number;
    total: number;
  };
  maquina: Maquina | null;
  pastaLogs: string;
  arquivos: number;
  ligadoEm: number;
  olhando: number;
}

type Conexao = "ligando" | "ao-vivo" | "reconectando";

/** Quantas linhas a tela guarda; passou disso, as mais velhas saem por cima. */
const LIMITE_LINHAS = 1500;
/** Quatro pinturas por segundo é o que separa "ao vivo" de "travado". */
const INTERVALO_PINTURA_MS = 250;

/* ------------------------------------------------------------------ *
 *  Formatos                                                            *
 * ------------------------------------------------------------------ */

function hora(em: number): string {
  return new Date(em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function haQuanto(em: number | null, agora: number): string {
  if (!em) return "—";
  const s = Math.max(0, Math.round((agora - em) / 1000));
  if (s < 5) return "agora";
  if (s < 60) return "há " + s + " s";
  const m = Math.round(s / 60);
  if (m < 60) return "há " + m + " min";
  const h = Math.round(m / 60);
  if (h < 48) return "há " + h + " h";
  return "há " + Math.round(h / 24) + " dias";
}

function duracao(segundos: number): string {
  if (segundos < 3600) return Math.round(segundos / 60) + " min";
  if (segundos < 86400) return Math.round(segundos / 3600) + " h";
  const d = Math.floor(segundos / 86400);
  return d + (d === 1 ? " dia" : " dias");
}

function gb(n: number | null): string {
  if (n === null) return "—";
  return (n / 1024 ** 3).toFixed(1).replace(".", ",") + " GB";
}

const pct = (a: number | null, b: number | null) => (a === null || !b ? null : Math.round((a / b) * 100));

/** "A, B, C e mais 4" — o cartão não pode virar uma lista de catorze nomes. */
function listaCurta(nomes: string[], maximo = 3): string {
  if (nomes.length <= maximo) return nomes.join(", ");
  return nomes.slice(0, maximo).join(", ") + " e mais " + (nomes.length - maximo);
}

/** O nome curto que cabe na coluna do console. */
const ABREV: Record<string, string> = {
  portal: "Portal",
  rh: "RH",
  dp: "DP",
  qualidade: "Qualid.",
  almoxarifado: "Almox.",
  planejamento: "Planej.",
  operacional: "Gestão Op.",
  comercial: "Comercial",
  proposta: "Propostas",
  relatorios: "Relat.",
  laboratorio: "Lab",
  colaborador: "Operac.",
  desenvolvedor: "Dev",
  suprimentos: "Suprim.",
  ponto: "Colabs.",
};

/**
 * Uma cor por sistema, só na etiqueta do console — é o que deixa o olho seguir
 * um sistema no meio dos outros sem ler o nome a cada linha. Tons de meia
 * saturação, nenhum vermelho: vermelho é do erro.
 */
const MATIZ: Record<string, number> = {
  portal: 150,
  rh: 205,
  dp: 260,
  qualidade: 180,
  almoxarifado: 30,
  planejamento: 225,
  operacional: 95,
  comercial: 285,
  proposta: 320,
  relatorios: 190,
  laboratorio: 170,
  colaborador: 60,
  desenvolvedor: 140,
  suprimentos: 45,
  ponto: 245,
};

function estiloDoSistema(id: string): React.CSSProperties {
  let h = MATIZ[id];
  if (h === undefined) {
    h = 0;
    for (const c of id) h = (h * 31 + c.charCodeAt(0)) % 360;
  }
  return { ["--mon-h" as string]: String(h) } as React.CSSProperties;
}

const ROTULO_STATUS: Record<Status, string> = {
  ok: "no ar",
  parcial: "parcial",
  fora: "fora do ar",
  desconhecido: "sem resposta ainda",
};

/* ------------------------------------------------------------------ *
 *  Pedaços da tela (fora do componente: definidos dentro, remontam a    *
 *  cada tecla digitada e a página salta — já aconteceu neste sistema)   *
 * ------------------------------------------------------------------ */

/** O fio da última meia hora dentro do cartão. */
function Fio({ dados, classe = "" }: { dados: number[]; classe?: string }) {
  if (!dados || dados.length < 2) return null;
  const maior = Math.max(...dados, 1);
  const passo = 100 / (dados.length - 1);
  const pontos = dados.map((v, i) => i * passo + "," + (100 - (v / maior) * 100));
  return (
    <svg className={"aud-spark mon-spark " + classe} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <polygon className="aud-spark-area" points={"0,100 " + pontos.join(" ") + " 100,100"} />
      <polyline className="aud-spark-linha" points={pontos.join(" ")} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Medidor({ rotulo, valor, detalhe }: { rotulo: string; valor: number | null; detalhe: string }) {
  const faixa = valor === null ? "" : valor >= 90 ? " alto" : valor >= 75 ? " medio" : "";
  return (
    <span className="mon-medidor" title={detalhe}>
      <span className="mon-medidor-rotulo">{rotulo}</span>
      <span className="mon-medidor-trilho">
        <i className={"mon-medidor-fill" + faixa} style={{ width: (valor ?? 0) + "%" }} />
      </span>
      <span className="mon-medidor-valor">{valor === null ? "—" : valor + "%"}</span>
    </span>
  );
}

function Farol({ status }: { status: Status }) {
  return <span className={"mon-farol " + status} aria-label={ROTULO_STATUS[status]} />;
}

function CartaoSistema({
  s,
  ativo,
  agora,
  onClick,
}: {
  s: SistemaResumo;
  ativo: boolean;
  agora: number;
  onClick: () => void;
}) {
  const { saude } = s;
  const parciais = saude.detalhes.filter((d) => !d.ok).map((d) => d.nome);
  let situacao: string;
  if (saude.status === "ok") situacao = "no ar" + (saude.ms !== null ? " · " + saude.ms + " ms" : "");
  else if (saude.status === "parcial") situacao = "parcial: " + parciais.join(", ") + " sem responder";
  else if (saude.status === "fora") situacao = "fora do ar" + (saude.desde ? " " + haQuanto(saude.desde, agora) : "");
  else situacao = ROTULO_STATUS.desconhecido;

  return (
    <button
      type="button"
      className={"aud-sistema mon-sistema" + (ativo ? " ativo" : "") + " st-" + saude.status}
      onClick={onClick}
      title={ativo ? "Mostrar todos no console" : "Só " + s.nome + " no console"}
    >
      <span className="mon-sistema-topo">
        <Farol status={saude.status} />
        <span className="aud-sistema-nome">{s.nome}</span>
        <span className="mon-sistema-porta">:{s.porta}</span>
      </span>
      <span className="aud-sistema-linha">
        <span className="aud-sistema-valor">{s.linhasMin}</span>
        <span className="aud-sistema-unidade">linhas/min</span>
        <span className={"mon-sistema-erros" + (s.erros5min > 0 ? " tem" : "")}>
          {s.erros5min > 0 && <IconAlert size={12} />}
          {s.erros5min} {s.erros5min === 1 ? "erro" : "erros"}
        </span>
      </span>
      <Fio dados={s.serie} classe={s.erros5min > 0 ? "com-erro" : ""} />
      <span className="aud-sistema-pe">
        <span className={"mon-situacao " + saude.status}>{situacao}</span>
        <span>
          {!s.temLog
            ? "sem arquivo de log"
            : s.ultimaLinhaEm
              ? "última linha " + haQuanto(s.ultimaLinhaEm, agora)
              : "quieto desde que liguei"}
        </span>
        {saude.quedas24h > 0 && (
          <span className="falha">
            {saude.quedas24h} {saude.quedas24h === 1 ? "queda" : "quedas"} em 24 h
          </span>
        )}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ *
 *  A tela                                                              *
 * ------------------------------------------------------------------ */

export default function MonitorView() {
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [conexao, setConexao] = useState<Conexao>("ligando");
  const [erro, setErro] = useState<string | null>(null);
  const [sistema, setSistema] = useState("todos");
  const [soErros, setSoErros] = useState(false);
  const [busca, setBusca] = useState("");
  const [pausado, setPausado] = useState(false);
  const [emEspera, setEmEspera] = useState(0);
  const [abertas, setAbertas] = useState<Set<number>>(() => new Set());
  const [seguir, setSeguir] = useState(true);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());
  /* Muda para religar o fluxo na mão (o botão "tentar de novo"). */
  const [versao, setVersao] = useState(0);

  const pendentes = useRef<Linha[]>([]);
  const pausadoRef = useRef(false);
  const ultimoId = useRef(0);
  const consoleRef = useRef<HTMLDivElement | null>(null);

  /* O relógio dos "há 5 s": um por tela, não um por cartão. */
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  /* A FILA → A TELA, quatro vezes por segundo. */
  useEffect(() => {
    const t = setInterval(() => {
      const fila = pendentes.current;
      if (fila.length === 0) return;
      if (pausadoRef.current) {
        setEmEspera(fila.length);
        return;
      }
      pendentes.current = [];
      setLinhas((atual) => {
        const juntas = atual.concat(fila);
        return juntas.length > LIMITE_LINHAS ? juntas.slice(juntas.length - LIMITE_LINHAS) : juntas;
      });
    }, INTERVALO_PINTURA_MS);
    return () => clearInterval(t);
  }, []);

  /* O FLUXO. */
  useEffect(() => {
    let vivo = true;
    let tentativa = 0;
    const controle = new AbortController();

    const tratarBloco = (bloco: string) => {
      let evento = "message";
      const dados: string[] = [];
      for (const l of bloco.split("\n")) {
        if (l.startsWith(":")) continue;
        if (l.startsWith("event:")) evento = l.slice(6).trim();
        else if (l.startsWith("data:")) dados.push(l.slice(5).replace(/^ /, ""));
      }
      if (dados.length === 0) return;
      let obj: any;
      try {
        obj = JSON.parse(dados.join("\n"));
      } catch {
        return;
      }
      if (evento === "ola") {
        setResumo(obj.resumo as Resumo);
        const chegaram = (obj.linhas ?? []) as Linha[];
        const maiorId = chegaram.reduce((m, l) => Math.max(m, l.id), 0);
        /* Primeira vez (ou o servidor reiniciou e os ids voltaram do zero):
           o que veio é a tela. Reconexão: emenda só o que ainda não tínhamos. */
        if (ultimoId.current === 0 || maiorId < ultimoId.current) {
          pendentes.current = [];
          setLinhas(chegaram);
        } else {
          pendentes.current.push(...chegaram.filter((l) => l.id > ultimoId.current));
        }
        ultimoId.current = Math.max(ultimoId.current, maiorId);
      } else if (evento === "linhas") {
        const lote = obj as Linha[];
        for (const l of lote) ultimoId.current = Math.max(ultimoId.current, l.id);
        pendentes.current.push(...lote);
      } else if (evento === "resumo") {
        setResumo(obj as Resumo);
      }
    };

    const ligar = async () => {
      while (vivo) {
        try {
          setConexao(tentativa === 0 ? "ligando" : "reconectando");
          const res = await abrirFluxoMonitor(controle.signal);
          if (!res.ok || !res.body) {
            let msg = "Não foi possível abrir o monitor (HTTP " + res.status + ").";
            try {
              const corpo = await res.json();
              if (corpo?.error) msg = corpo.error;
            } catch {
              /* sem JSON */
            }
            if (res.status === 401 || res.status === 403) {
              setErro(msg);
              return;
            }
            throw new ApiError(msg, res.status);
          }
          setErro(null);
          setConexao("ao-vivo");
          tentativa = 0;
          const leitor = res.body.getReader();
          const dec = new TextDecoder();
          let buf = "";
          for (;;) {
            const { value, done } = await leitor.read();
            if (done) break;
            buf += dec.decode(value, { stream: true });
            let corte: number;
            while ((corte = buf.indexOf("\n\n")) >= 0) {
              tratarBloco(buf.slice(0, corte));
              buf = buf.slice(corte + 2);
            }
          }
        } catch {
          /* rede, deploy, servidor reiniciando: tenta de novo abaixo */
        }
        if (!vivo) return;
        setConexao("reconectando");
        tentativa++;
        await new Promise((r) => setTimeout(r, Math.min(30_000, 1000 * 2 ** Math.min(tentativa, 5))));
      }
    };
    void ligar();
    return () => {
      vivo = false;
      controle.abort();
    };
  }, [versao]);

  /* O que aparece: o filtro é só da tela, o fluxo traz tudo. */
  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return linhas.filter(
      (l) =>
        (sistema === "todos" || l.sistema === sistema) &&
        (!soErros || l.nivel === "erro") &&
        (!q || l.texto.toLowerCase().includes(q) || (ABREV[l.sistema] ?? l.sistema).toLowerCase().includes(q)),
    );
  }, [linhas, sistema, soErros, busca]);

  /* Segue o fim enquanto a pessoa não rolou para cima. */
  useEffect(() => {
    const el = consoleRef.current;
    if (el && seguir && !pausado) el.scrollTop = el.scrollHeight;
  }, [visiveis, seguir, pausado]);

  const aoRolar = useCallback(() => {
    const el = consoleRef.current;
    if (!el) return;
    setSeguir(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
  }, []);

  const irParaOFim = () => {
    setSeguir(true);
    const el = consoleRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const alternarPausa = () => {
    const agoraPausado = !pausadoRef.current;
    pausadoRef.current = agoraPausado;
    setPausado(agoraPausado);
    if (!agoraPausado) {
      setEmEspera(0);
      setSeguir(true);
    }
  };

  const limpar = () => {
    pendentes.current = [];
    setLinhas([]);
    setEmEspera(0);
    setAbertas(new Set());
  };

  const alternarAberta = (id: number) =>
    setAbertas((atual) => {
      const n = new Set(atual);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  /** Mais 300 linhas de antes, direto do arquivo do sistema escolhido. */
  const carregarAntigas = async () => {
    if (sistema === "todos" || carregandoAntigas) return;
    setCarregandoAntigas(true);
    try {
      const r = (await api.monitor.historico(sistema, 300)) as {
        arquivos: { canal: Canal; linhas: Linha[] }[];
      };
      const antigas = r.arquivos.flatMap((a) => a.linhas);
      const jaTem = new Set(linhas.map((l) => l.texto + "|" + l.sistema + "|" + l.canal));
      const novas = antigas.filter((l) => !jaTem.has(l.texto + "|" + l.sistema + "|" + l.canal));
      setSeguir(false);
      setLinhas((atual) => novas.concat(atual).slice(-LIMITE_LINHAS - novas.length));
      const el = consoleRef.current;
      if (el) el.scrollTop = 0;
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Não foi possível ler o arquivo de log.");
    } finally {
      setCarregandoAntigas(false);
    }
  };

  if (erro && !resumo) {
    return (
      <ErrorState
        message={erro}
        onRetry={() => {
          setErro(null);
          setVersao((v) => v + 1);
        }}
      />
    );
  }

  const t = resumo?.totais;
  const m = resumo?.maquina ?? null;
  const sistemas = resumo ? ordenarPorNome(resumo.sistemas, (s) => s.nome) : [];
  const foraDoAr = sistemas.filter((s) => s.saude.status === "fora");
  const parciais = sistemas.filter((s) => s.saude.status === "parcial");
  const tomNoAr = t && t.fora > 0 ? "tone-red" : t && t.parciais > 0 ? "tone-amber" : "tone-green";
  const picoLinhas = t ? Math.max(...t.serie, 0) : 0;
  const ultimoErroGeral = sistemas
    .map((s) => (s.ultimoErro ? { ...s.ultimoErro, sistema: s.nome } : null))
    .filter(Boolean)
    .sort((a, b) => b!.em - a!.em)[0];
  const cpu = m?.cpu ?? null;
  const ram = m ? pct(m.memUsada, m.memTotal) : null;
  const disco = m ? pct(m.discoUsado, m.discoTotal) : null;

  const estadoTexto = pausado
    ? "pausado · " + (emEspera > 0 ? emEspera + " linha(s) esperando" : "nada novo ainda")
    : conexao === "ao-vivo"
      ? "ao vivo · " +
        (resumo ? resumo.arquivos + " arquivos em " + resumo.pastaLogs : "ligando…") +
        (resumo && resumo.olhando > 1 ? " · " + resumo.olhando + " pessoas olhando" : "")
      : conexao === "ligando"
        ? "ligando…"
        : "sem conexão com o servidor — tentando de novo";

  const indiceVivo = visiveis.findIndex((l) => !l.antes);

  return (
    <>
      {/* ---------- OS QUATRO INDICADORES ---------- */}
      <div className="cards">
        <div className={"card " + tomNoAr}>
          <span className="card-icon">
            {t && t.fora + t.parciais > 0 ? <IconAlert size={22} /> : <IconShieldCheck size={22} />}
          </span>
          <span className="card-body">
            <span className="card-label">Sistemas no ar</span>
            <span className="card-value">
              {t ? t.noAr : "—"}
              <small className="mon-de">/{t ? t.total : "—"}</small>
            </span>
            <small className={"aud-card-nota" + (foraDoAr.length ? " forte" : "")}>
              {!t
                ? "conferindo…"
                : foraDoAr.length
                  ? "fora: " + listaCurta(foraDoAr.map((s) => s.nome))
                  : parciais.length
                    ? "parcial: " + listaCurta(parciais.map((s) => s.nome))
                    : "todos respondendo"}
            </small>
          </span>
        </div>

        <div className="card tone-teal">
          <span className="card-icon">
            <IconActivity size={22} />
          </span>
          <span className="card-body">
            <span className="card-label">Linhas por minuto</span>
            <span className="card-value">{t ? t.linhasMin : "—"}</span>
            {t && <Fio dados={t.serie} />}
            <small className="aud-card-nota">
              {t ? "pico de " + picoLinhas + "/min nos últimos 30 min" : "…"}
            </small>
          </span>
        </div>

        <div className={"card " + (t && t.erros5min > 0 ? "tone-red" : "tone-emerald")}>
          <span className="card-icon">{t && t.erros5min > 0 ? <IconAlert size={22} /> : <IconCheck size={22} />}</span>
          <span className="card-body">
            <span className="card-label">Erros (5 min)</span>
            <span className="card-value">{t ? t.erros5min : "—"}</span>
            {t && <Fio dados={t.serieErros} classe="com-erro" />}
            <small className={"aud-card-nota" + (t && t.erros5min > 0 ? " forte" : "")}>
              {ultimoErroGeral
                ? "último: " + ultimoErroGeral.sistema + " " + haQuanto(ultimoErroGeral.em, agora)
                : "nenhum erro desde que o monitor ligou"}
            </small>
          </span>
        </div>

        <div className="card tone-lime mon-card-maquina">
          <span className="card-icon">
            <IconGauge size={22} />
          </span>
          <span className="card-body">
            <span className="card-label">
              Máquina
              {m && (
                <small className="muted">
                  {" "}
                  · {m.nucleos} núcleos · ligada há {duracao(m.ligadaHa)}
                </small>
              )}
            </span>
            <Medidor
              rotulo="CPU"
              valor={cpu}
              detalhe={m?.carga !== null && m?.carga !== undefined ? "carga média (1 min): " + m.carga : "uso do processador"}
            />
            <Medidor rotulo="RAM" valor={ram} detalhe={m ? gb(m.memUsada) + " de " + gb(m.memTotal) : ""} />
            <Medidor
              rotulo="Disco"
              valor={disco}
              detalhe={
                m && m.discoTotal !== null
                  ? gb(m.discoUsado) + " de " + gb(m.discoTotal) + (m.discoOnde ? " em " + m.discoOnde : "")
                  : "medido só na VPS"
              }
            />
          </span>
        </div>
      </div>

      {/* ---------- OS SISTEMAS ---------- */}
      <section className="aud-bloco">
        <div className="aud-head">
          <span className="aud-head-icone">
            <IconActivity size={17} />
          </span>
          <span className="aud-head-txt">
            <h3>Os sistemas agora</h3>
            <small>farol da porta a cada 15 s · linhas por minuto · erros nos últimos 5 min · tendência de meia hora</small>
          </span>
          {sistema !== "todos" && (
            <span className="aud-head-acao">
              <button className="aud-chip" onClick={() => setSistema("todos")} title="Mostrar todos no console">
                só <b>{sistemas.find((s) => s.id === sistema)?.nome ?? sistema}</b>
                <IconClose size={13} />
              </button>
            </span>
          )}
        </div>
        {sistemas.length === 0 ? (
          <p className="aud-vazio">Ligando o monitor…</p>
        ) : (
          <div className="aud-sistemas mon-grade">
            {sistemas.map((s) => (
              <CartaoSistema
                key={s.id}
                s={s}
                agora={agora}
                ativo={sistema === s.id}
                onClick={() => setSistema((atual) => (atual === s.id ? "todos" : s.id))}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---------- O CONSOLE ---------- */}
      <section className="aud-bloco mon-bloco-console">
        <div className="aud-head">
          <span className={"mon-vivo " + (pausado ? "pausado" : conexao)}>
            <i />
          </span>
          <span className="aud-head-txt">
            <h3>Console ao vivo</h3>
            <small>{estadoTexto}</small>
          </span>
          <span className="aud-head-acao">
            {pausado && emEspera > 0 && (
              <button className="aud-chip" onClick={alternarPausa} title="Continuar e mostrar o que chegou">
                +<b>{emEspera}</b> novas
                <IconChevronRight size={13} />
              </button>
            )}
            <button className="btn-secondary btn-sm" onClick={alternarPausa} title={pausado ? "Voltar a mostrar as linhas novas" : "Segurar a tela (as linhas continuam chegando)"}>
              {pausado ? <IconPlay size={15} /> : <IconPause size={15} />}
              {pausado ? "Continuar" : "Pausar"}
            </button>
            <button className="btn-secondary btn-sm" onClick={limpar} title="Limpar a tela (os arquivos continuam intactos)">
              <IconTrash size={15} />
              Limpar
            </button>
          </span>
        </div>

        <div className="aud-filtros mon-filtros">
          <select value={sistema} onChange={(e) => setSistema(e.target.value)} aria-label="Sistema">
            <option value="todos">Todos os sistemas</option>
            {sistemas.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={"mon-toggle" + (soErros ? " ativo" : "")}
            onClick={() => setSoErros((v) => !v)}
            aria-pressed={soErros}
          >
            <IconAlert size={14} />
            Só erros
          </button>
          <label className="search-wrap">
            <IconSearch size={16} className="search-icone" />
            <input
              type="search"
              placeholder="Buscar no que está na tela…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </label>
        </div>

        <div className="mon-console" ref={consoleRef} onScroll={aoRolar} role="log" aria-live="off">
          {visiveis.length === 0 ? (
            <p className="mon-vazio">
              {linhas.length === 0
                ? "Nenhuma linha ainda. Os sistemas escrevem no log quando algo acontece — subida, erro, e-mail enviado. Sistema quieto é sistema bem."
                : "Nada bate com o filtro."}
            </p>
          ) : (
            visiveis.map((l, i) => (
              <div key={l.id}>
                {i === indiceVivo && i > 0 && <div className="mon-divisor">ao vivo a partir daqui</div>}
                <div
                  className={
                    "mon-linha nv-" + l.nivel + (l.antes ? " antes" : "") + (abertas.has(l.id) ? " aberta" : "")
                  }
                  onClick={() => alternarAberta(l.id)}
                  title={l.canal === "erro" ? "veio do log de erros (stderr)" : undefined}
                >
                  <span className="mon-hora">{l.em ? hora(l.em) : "antes"}</span>
                  <span className="mon-sis" style={estiloDoSistema(l.sistema)}>
                    {ABREV[l.sistema] ?? l.sistema}
                  </span>
                  <span className="mon-txt">{l.texto}</span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mon-rodape">
          <span className="muted">
            {visiveis.length === linhas.length
              ? linhas.length + " linha(s) na tela"
              : visiveis.length + " de " + linhas.length + " linha(s)"}
            {linhas.length >= LIMITE_LINHAS ? " · guardando as últimas " + LIMITE_LINHAS : ""}
          </span>
          <span className="mon-rodape-acoes">
            {sistema !== "todos" && (
              <button className="btn-link" onClick={carregarAntigas} disabled={carregandoAntigas}>
                {carregandoAntigas ? "lendo o arquivo…" : "ver 300 linhas anteriores do arquivo"}
              </button>
            )}
            {!seguir && !pausado && (
              <button className="btn-link" onClick={irParaOFim}>
                ir para o fim
              </button>
            )}
          </span>
        </div>
      </section>
    </>
  );
}
